import { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Box,
  CircularProgress,
} from "@mui/material";
import { getUserPlaylists } from "../api";

export default function CopyPlaylistModal(props) {
  const [state, setState] = useState({
    selected: [],
    isLoading: true,
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setState((s) => ({
        ...s,
        role: user.role,
      }));
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const rawPlaylists = await getUserPlaylists({
          eventId: props.eventId,
          venueId: props.venueId,
        });

        const userPlaylists = rawPlaylists.filter(
          (x) => x.id !== props.playlistId
        );

        setState((s) => ({
          ...s,
          userPlaylists,
          isLoading: false,
        }));
      } catch (error) {
        console.error("Error loading playlists:", error);
        setState((s) => ({
          ...s,
          isLoading: false,
          error: "Failed to load playlists",
        }));
      }
    })();
  }, [props.playlistId, props.eventId, props.venueId]);

  const handleDownload = async () => {
    const element = document.createElement("a");
    const file = new Blob([state.playlist.map((x) => x.item).join("\n")], {
      type: "text/plain",
    });
    element.href = URL.createObjectURL(file);
    element.download = "playlist.txt";
    document.body.appendChild(element);
    element.click();
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setState((s) => ({
        ...s,
        selected: state.playlist.map((_, index) => index),
      }));
    } else {
      setState((s) => ({
        ...s,
        selected: [],
      }));
    }
  };

  const handleSelect = (index) => {
    setState((s) => {
      const selectedIndex = s.selected.indexOf(index);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = [...s.selected, index];
      } else {
        newSelected = s.selected.filter((i) => i !== index);
      }

      return {
        ...s,
        selected: newSelected,
      };
    });
  };

  const handleSelectedPlaylistChange = async (e, value) => {
    const playlist = state.userPlaylists.find(
      (x) => x.id === value.value
    ).playlist;

    setState((s) => ({
      ...s,
      playlist,
      selected: playlist.map((_, index) => index), // Select all songs by default
    }));
  };

  return (
    <Dialog
      open={true}
      onClose={props.handleClose}
      PaperProps={{
        style: {
          backgroundColor: "#d8c8f4",
          color: "#4b445b",
          width: "1200px",
          height: "600px",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle>Copy Playlist</DialogTitle>
      <DialogContent>
        {state.isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              minHeight: "400px",
            }}
          >
            <CircularProgress sx={{ color: "#4b445b" }} />
          </Box>
        ) : state.error ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              minHeight: "400px",
              color: "error.main",
            }}
          >
            <Typography>{state.error}</Typography>
          </Box>
        ) : (
          <>
            {!!state.userPlaylists && (
              <Autocomplete
                disablePortal
                id="selectedPlaylist-dropdown"
                options={state.userPlaylists.map((x) => ({
                  label: `${x.venueName} / ${x.eventDate.slice(
                    8,
                    10
                  )}/${x.eventDate.slice(5, 7)}/${x.eventDate.slice(
                    0,
                    4
                  )} / ${new Date(x.startTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })} - ${new Date(x.endTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`,
                  value: x.id,
                }))}
                disableClearable
                onChange={handleSelectedPlaylistChange}
                renderInput={(params) => (
                  <TextField
                    margin="dense"
                    fullWidth
                    variant="outlined"
                    label="Playlist"
                    id="selectedPlaylist"
                    type="text"
                    {...params}
                  />
                )}
              />
            )}
            {state.playlist && (
              <TableContainer
                component={Paper}
                sx={{
                  marginTop: 2,
                  backgroundColor: "transparent",
                  height: "350px",
                  "& .MuiTableCell-root": {
                    color: "#4b445b",
                  },
                }}
              >
                <Table stickyHeader aria-label="playlist songs table">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        padding="checkbox"
                        sx={{ backgroundColor: "#c4b4e0" }}
                      >
                        <Checkbox
                          color="primary"
                          indeterminate={
                            state.selected.length > 0 &&
                            state.selected.length < state.playlist.length
                          }
                          checked={
                            state.playlist?.length > 0 &&
                            state.selected.length === state.playlist.length
                          }
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#c4b4e0" }}>
                        Song Name
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.playlist.map((song, index) => (
                      <TableRow
                        key={index}
                        selected={state.selected.indexOf(index) !== -1}
                        onClick={() => handleSelect(index)}
                        hover
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={state.selected.indexOf(index) !== -1}
                          />
                        </TableCell>
                        <TableCell>{song.item}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {["admin"].includes(state.role) && (
          <Button
            onClick={handleDownload}
            variant="contained"
            sx={{ fontSize: "0.7rem" }}
            disabled={state.isLoading || !state.playlist}
          >
            Download
          </Button>
        )}
        <Button
          onClick={props.handleClose}
          variant="contained"
          sx={{ fontSize: "0.7rem" }}
        >
          Cancel
        </Button>
        <Button
          onClick={() =>
            props.handleConfirm(
              state.playlist.filter((_, index) =>
                state.selected.includes(index)
              ),
              true
            )
          }
          variant="contained"
          sx={{ fontSize: "0.7rem" }}
          disabled={state.isLoading || state.selected.length === 0}
        >
          Add to current playlist
        </Button>
        <Button
          onClick={() =>
            props.handleConfirm(
              state.playlist.filter((_, index) =>
                state.selected.includes(index)
              ),
              false
            )
          }
          variant="contained"
          sx={{ fontSize: "0.7rem" }}
          disabled={state.isLoading || state.selected.length === 0}
        >
          Replace current playlist
        </Button>
      </DialogActions>
    </Dialog>
  );
}
